import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../services/api';
import { useCity } from '../contexts/CityContext';
import styles from './BusLineDetailPage.module.css';
import LineChip from '../components/common/LineChip';
import 'leaflet/dist/leaflet.css';
import { 
  MdDirectionsBus, 
  MdDirections, 
  MdArrowForward, MdArrowBack, 
  MdWarning, MdStop, MdAnnouncement,
  MdLocationOn, MdCommute, // Durak ve araç için yeni ikonlar eklendi
} from 'react-icons/md';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { decodePolyline } from '../utils/transportUtils';

// Marker ikonlarını düzelt
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

// React-Icons kullanarak durak ikonu oluşturma
const createStopIcon = () => {
  const stopIconHtml = `
    <div style="
      width: 28px; 
      height: 28px; 
      display: flex; 
      justify-content: center; 
      align-items: center; 
      background-color: #ffffff;
      border-radius: 50%;
      border: 2px solid #1976d2;
      box-shadow: 0 2px 5px rgba(0,0,0,0.2);
    ">
      <svg viewBox="0 0 24 24" style="width: 16px; height: 16px; fill: #1976d2">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"></path>
      </svg>
    </div>
  `;

  return L.divIcon({
    html: stopIconHtml,
    className: '',
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28]
  });
}

// React-Icons kullanarak araç ikonu oluşturma
const createVehicleIcon = () => {
  const vehicleIconHtml = `
    <div style="
      width: 32px; 
      height: 32px; 
      display: flex; 
      justify-content: center; 
      align-items: center; 
      background-color: #1976d2;
      border-radius: 50%;
      box-shadow: 0 2px 5px rgba(0,0,0,0.2);
    ">
      <svg viewBox="0 0 24 24" style="width: 20px; height: 20px; fill: white">
        <path d="M4 16c0 .88.39 1.67 1 2.22V20c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h8v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1.78c.61-.55 1-1.34 1-2.22V6c0-3.5-3.58-4-8-4s-8 .5-8 4v10zm3.5 1c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm9 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm1.5-6H6V6h12v5z"></path>
      </svg>
    </div>
  `;

  return L.divIcon({
    html: vehicleIconHtml,
    className: '',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16]
  });
}

// Statik ikon nesneleri yerine fonksiyon kullanarak gerçek zamanlı oluşturma
const busStopIcon = createStopIcon();
const vehicleIcon = createVehicleIcon();

// Geliştirilmiş Map Controller Bileşeni
const MapController = ({ path, stops }: { path: [number, number][] | undefined, stops: BusStop[] | undefined }) => {
  const map = useMap();
  
  useEffect(() => {
    // Harita odaklanması için kısa bir gecikme ekleyelim (haritanın tam yüklenmesini bekler)
    const timer = setTimeout(() => {
      try {
        if (path && path.length > 0) {
          // Güzergah varsa, güzergahı ve durakları kapsayan sınırlar oluştur
          const bounds = L.latLngBounds(path.map(point => L.latLng(point[0], point[1])));
          
          // Durakları da sınırlara ekle
          if (stops && stops.length > 0) {
            stops.forEach(stop => {
              if (stop.latitude && stop.longitude) {
                bounds.extend([stop.latitude, stop.longitude]);
              }
            });
          }
          
          // Sınırların geçerli olduğundan emin ol
          if (bounds.isValid()) {
            console.log('Harita odaklanıyor - Güzergah', bounds);
            map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
          } else {
            console.warn('Geçersiz sınırlar, durakları odakla.');
            // Sadece durakları odakla
            focusStops();
          }
        } else {
          // Sadece durak odaklama
          focusStops();
        }
      } catch (error) {
        console.error('Harita odaklama hatası:', error);
        // Varsayılan görünüme geç
        map.setView([38.41, 27.13], 12);
      }
    }, 500);
    
    // Sadece durakları odaklama fonksiyonu
    function focusStops() {
      if (stops && stops.length > 0) {
        try {
          const stopBounds = L.latLngBounds(
            stops
              .filter(stop => stop.latitude && stop.longitude)
              .map(stop => L.latLng(stop.latitude, stop.longitude))
          );
          
          if (stopBounds.isValid()) {
            console.log('Harita durak odaklı', stopBounds);
            map.fitBounds(stopBounds, { padding: [50, 50], maxZoom: 15 });
          } else {
            // Sınırlar geçersizse varsayılan görünüme git
            map.setView([38.41, 27.13], 12);
          }
        } catch (error) {
          console.error('Durak odaklama hatası:', error);
          map.setView([38.41, 27.13], 12);
        }
      } else {
        map.setView([38.41, 27.13], 12);
      }
    }
    
    // Temizleme işlevi
    return () => {
      clearTimeout(timer);
    };
  }, [map, path, stops]);
  
  return null;
};

// Arayüz tanımlamaları
interface Operator {
  operator_id: number;
  operator_name: string;
  operator_logo?: string;
  operator_website?: string;
  operator_phone?: string;
}

interface LiveVehicle {
  vehicle_id: number;
  direction_id: number;
  latitude: number;
  longitude: number;
  updated_at: string;
}

interface Announcement {
  announcement_id: number;
  bus_line_id: string;
  title: string;
  description?: string;
  start_date: string;
  end_date: string;
  updated_at: string;
}

interface BusStop {
  bus_stop_id: number;
  bus_stop_name: string;
  latitude: number;
  longitude: number;
  bus_stop_sequence: number;
}

interface Direction {
  direction_id: number;
  direction_name: string;
  direction_headsign: string;
  bus_stops?: BusStop[];
  route_geometry?: string;
  geometry_precision?: number;
  decoded_path?: [number, number][];
}

interface BusLine {
  bus_line_id: string;
  bus_line_name: string;
  bus_line_path_description: string;
  description: string;
  start_bus_stop: string;
  end_bus_stop: string;
  bus_line_color: string;
  active: boolean;
  operator: Operator;
  live_vehicles?: LiveVehicle[];
  bus_line_announcements?: Announcement[];
  directions?: Direction[];
}

function formatDateTime(isoDateTime: string): string {
  try {
    const date = new Date(isoDateTime);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return isoDateTime;
  }
}

function BusLineDetailPage() {
  const { lineId } = useParams<{ lineId: string }>();
  const [lineDetail, setLineDetail] = useState<BusLine | null>(null);
  const [lineStops, setLineStops] = useState<Direction[] | null>(null);
  const [linePath, setLinePath] = useState<Direction[] | null>(null);
  const [selectedDirection, setSelectedDirection] = useState<number>(1); // Varsayılan olarak gidiş
  const [loading, setLoading] = useState<boolean>(true);
  const [pathLoading, setPathLoading] = useState<boolean>(false);
  const [stopsLoading, setStopsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedStopId, setSelectedStopId] = useState<string | null>(null);
  const [stopsLoaded, setStopsLoaded] = useState<boolean>(false);
  const [retryCount, setRetryCount] = useState<number>(0);
  const [isRetrying, setIsRetrying] = useState<boolean>(false);
  const lastRefreshTimeRef = useRef<Date>(new Date());
  const mapRef = useRef<L.Map | null>(null);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialMount = useRef(true);
  const dataLoadedRef = useRef({
    lineDetails: false,
    linePath: false,
    lineStops: false
  });
  
  const api = useApi();
  const { currentCity, loading: cityLoading } = useCity();
  const navigate = useNavigate();
  
  // Hat detaylarını al - geliştirilmiş hata yönetimi ile
  const fetchLineDetail = useCallback(async (showIndicator = true) => {
    if (!lineId || !currentCity) return;
    
    if (showIndicator) {
      setLoading(true);
    }
    setError(null);
    setIsRetrying(false);
    
    try {
      console.log('Hat detayları yükleniyor. Hat ID:', lineId, 'Deneme:', retryCount + 1);
      
      const response = await api.getBusLine(lineId);
      
      if (response && response.status === "success" && response.data) {
        console.log('Hat detayları başarıyla alındı:', response.data);
        setLineDetail(response.data);
        setRetryCount(0); // Başarılı olduğunda sayacı sıfırla
        dataLoadedRef.current.lineDetails = true;
        // Yenileme zamanını güncelle
        lastRefreshTimeRef.current = new Date();
      } else {
        console.error('Hat detayları alınamadı veya boş yanıt:', response);
        setError('Hat detayları yüklenemedi.');
      }
    } catch (err: any) {
      console.error('Hat detayları yüklenirken hata:', err);
      
      if (err.message && err.message.includes('zaman aşımına uğradı')) {
        // Otomatik yeniden deneme mantığı
        if (retryCount < 2) {
          console.log(`Otomatik yeniden deneme ${retryCount + 1}/2`);
          setRetryCount(prev => prev + 1);
          setIsRetrying(true);
          setError(`Sunucu yanıt vermedi. Otomatik olarak yeniden deneniyor... (${retryCount + 1}/2)`);
          
          // 2 saniye sonra otomatik yeniden deneme yap
          setTimeout(() => {
            fetchLineDetail();
          }, 2000);
          return;
        }
      }
      
      // Maksimum deneme sayısına ulaşıldıysa veya başka bir hata olduysa
      setError(`Hat detayları yüklenirken hata oluştu: ${err.message || 'Bilinmeyen hata'}. Lütfen sayfayı yenileyip tekrar deneyin.`);
      if (showIndicator) {
        setLoading(false);
      }
    } finally {
      if (!isRetrying && showIndicator) {
        setLoading(false);
      }
    }
  }, [lineId, currentCity, api, retryCount]);
  
  // Hat duraklarını al 
  const fetchLineStops = useCallback(async () => {
    if (!lineId || !currentCity || stopsLoaded) return;
    
    setStopsLoading(true);
    
    try {
      console.log('Hat durakları yükleniyor. Hat ID:', lineId);
      
      const response = await api.getBusLineStops(lineId);
      
      if (response && response.status === "success" && response.data) {
        console.log('Hat durakları başarıyla alındı:', response.data);
        
        if (response.data.directions && response.data.directions.length > 0) {
          setLineStops(response.data.directions);
          
          // İlk durak ID'sini seç
          if (response.data.directions[0].bus_stops && response.data.directions[0].bus_stops.length > 0) {
            setSelectedStopId(response.data.directions[0].bus_stops[0].bus_stop_id.toString());
          }
          
          // Durakların yüklendiğini işaretle
          setStopsLoaded(true);
          dataLoadedRef.current.lineStops = true;
        }
      } else {
        console.error('Hat durakları alınamadı veya boş yanıt:', response);
      }
    } catch (err: any) {
      console.error('Hat durakları yüklenirken hata:', err);
    } finally {
      setStopsLoading(false);
    }
  }, [lineId, currentCity, api, stopsLoaded]);
  
  // Hat güzergahını al - güncellendi
  const fetchLinePath = useCallback(async () => {
    if (!lineId || !currentCity || (linePath && linePath.length > 0)) return;
    
    setPathLoading(true);
    
    try {
      console.log('Hat güzergahı yükleniyor. Hat ID:', lineId);
      
      const response = await api.getBusLinePath(lineId);
      
      if (response && response.status === "success" && response.data) {
        console.log('Hat güzergahı başarıyla alındı:', response.data);
        
        if (response.data.directions && response.data.directions.length > 0) {
          // Polyline'ları decode et - precision kullanımına dikkat et
          const decodedDirections = response.data.directions.map((direction: Direction) => {
            if (direction.route_geometry) {
              const precision = direction.geometry_precision || 5;
              console.log(`Rota çözümü: ${direction.direction_name}, hassasiyet: ${precision}`);
              return {
                ...direction,
                decoded_path: decodePolyline(direction.route_geometry, precision)
              };
            }
            return direction;
          });
          
          setLinePath(decodedDirections);
          dataLoadedRef.current.linePath = true;
        }
      } else {
        console.error('Hat güzergahı alınamadı veya boş yanıt:', response);
      }
    } catch (err: any) {
      console.error('Hat güzergahı yüklenirken hata:', err);
    } finally {
      setPathLoading(false);
    }
  }, [lineId, currentCity, api, linePath]);
  
  // Şehir yüklendikten sonra tüm verileri yükle
  useEffect(() => {
    if (!cityLoading && lineId && currentCity && isInitialMount.current) {
      // Tüm verileri paralel olarak yükle
      fetchLineDetail();
      fetchLinePath();
      fetchLineStops();
      isInitialMount.current = false;
    }
  }, [cityLoading, lineId, currentCity, fetchLineDetail, fetchLinePath, fetchLineStops]);
  
  // Sayfa kapanırken tüm zamanlayıcıları temizle
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, []);
  
  // Yeniden yükleme işlevi - manuel yeniden deneme
  const handleRetry = () => {
    setLoading(true);
    setError(null);
    setRetryCount(0);
    fetchLineDetail();
  };
  
  // Durak seçme işlevi
  const handleStopSelect = (stopId: string) => {
    setSelectedStopId(stopId);
  };

  return (
    <div className={styles.lineDetailPage}>
      {/* Üst Başlık ve Kontroller */}
      <div className={styles.backButtonContainer}>
        <button 
          className={styles.backButton}
          onClick={() => navigate('/lines')}
        >
          ← Hatlara Dön
        </button>
      </div>
      
      {/* Yükleme durumu */}
      {loading && (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <div className={styles.loadingMessage}>
            {cityLoading ? 'Şehir bilgileri yükleniyor...' : 'Hat detayları yükleniyor...'}
          </div>
        </div>
      )}
      
      {/* Hata durumu */}
      {error && !loading && (
        <div className={styles.errorContainer}>
          <div className={styles.errorMessage}>
            <div className={styles.errorIcon}><MdWarning /></div>
            <div className={styles.errorText}>
              <h3>Hata</h3>
              <p>{error}</p>
              {isRetrying ? (
                <div className={styles.retryingIndicator}>
                  <div className={styles.loadingSpinner}></div>
                  <span>Yeniden deneniyor...</span>
                </div>
              ) : (
                <button 
                  className={styles.retryButton}
                  onClick={handleRetry}
                >
                  Yeniden Dene
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Hat detay içeriği */}
      {!loading && !error && lineDetail && (
        <>
          {/* Hat Başlık Bilgileri */}
          <div className={styles.lineHeaderCard}>
            <div className={styles.lineHeader}>
              <div className={styles.lineHeaderLeft}>
                <div className={styles.modeIndicator}>
                  <MdDirectionsBus />
                  <span>Otobüs</span>
                </div>
                <LineChip 
                  lineNumber={lineDetail.bus_line_id} 
                  size="large" 
                  lineColor={lineDetail.bus_line_color || undefined}
                />
                <h1 className={styles.lineName}>{lineDetail.bus_line_name}</h1>
              </div>
            </div>
            
            <div className={styles.linePathDescription}>
              <MdDirections className={styles.pathIcon} />
              <span>{lineDetail.bus_line_path_description}</span>
            </div>
            
            {/* Canlı araç bilgileri */}
            {lineDetail.live_vehicles && lineDetail.live_vehicles.length > 0 && (
              <div className={styles.liveVehiclesInfo}>
                <div className={styles.liveVehiclesTitle}>
                  <MdDirectionsBus /> Hatta {lineDetail.live_vehicles.length} araç aktif
                </div>
                <div className={styles.liveVehiclesTime}>
                  Son güncelleme: {formatDateTime(lineDetail.live_vehicles[0].updated_at)}
                </div>
              </div>
            )}
          </div>
          
          {/* Hat duyuruları varsa göster */}
          {lineDetail.bus_line_announcements && lineDetail.bus_line_announcements.length > 0 && (
            <div className={styles.announcementsCard}>
              <div className={styles.cardHeader}>
                <MdAnnouncement /> <h3>Duyurular</h3>
              </div>
              <div className={styles.announcementsList}>
                {lineDetail.bus_line_announcements.map((announcement) => (
                  <div key={announcement.announcement_id} className={styles.announcementItem}>
                    <div className={styles.announcementHeader}>
                      <h4>{announcement.title}</h4>
                      <span>{formatDateTime(announcement.start_date)} - {formatDateTime(announcement.end_date)}</span>
                    </div>
                    {announcement.description && (
                      <p>{announcement.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Ana İçerik Alanı - Harita ve Duraklar */}
          <div className={styles.mainContentGrid}>
            {/* SOL TARAF - Harita */}
            <div className={styles.mapSection}>
              <div className={styles.sectionHeader}>
                <h3>Hat Güzergahı</h3>
                
                {/* Yön seçici */}
                <div className={styles.directionSelector}>
                  {linePath && linePath.map(direction => (
                    <button
                      key={direction.direction_id}
                      className={`${styles.directionButton} ${selectedDirection === direction.direction_id ? styles.activeDirection : ''}`}
                      onClick={() => setSelectedDirection(direction.direction_id)}
                    >
                      {direction.direction_id === 1 ? <MdArrowForward /> : <MdArrowBack />}
                      {direction.direction_name}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className={styles.mapContainer}>
                {pathLoading ? (
                  <div className={styles.loadingMapMessage}>
                    <div className={styles.loadingSpinner}></div>
                    <span>Güzergah yükleniyor...</span>
                  </div>
                ) : linePath && linePath.length > 0 ? (
                  <MapContainer 
                    center={[38.41, 27.13]} // Varsayılan merkez
                    zoom={12}
                    className={styles.leafletMap}
                    style={{ height: '100%', width: '100%' }}
                    whenReady={() => {
                      // Bileşen zaten map nesnesine erişip ref'e atar,
                      // İhtiyaç olmadığında whenReady boş bir fonksiyon olarak kullanabiliriz
                    }}
                    ref={(map) => {
                      // Map referansını bu şekilde almak daha güvenli
                      if (map && !mapRef.current) {
                        mapRef.current = map;
                        console.log('Harita referansı alındı');
                      }
                    }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    
                    {/* Güzergah çizgisi */}
                    {linePath.find(d => d.direction_id === selectedDirection)?.decoded_path && (
                      <Polyline
                        positions={linePath.find(d => d.direction_id === selectedDirection)?.decoded_path || []}
                        color={lineDetail.bus_line_color || '#FF0000'}
                        weight={5}
                        opacity={0.7}
                      />
                    )}
                    
                    {/* Duraklar - güncellenmiş ikonla */}
                    {lineStops && 
                      lineStops.find(d => d.direction_id === selectedDirection)?.bus_stops?.map((stop) => (
                        <Marker
                          key={stop.bus_stop_id}
                          position={[stop.latitude, stop.longitude]}
                          icon={busStopIcon}
                        >
                          <Popup>
                            <div className={styles.stopPopup}>
                              <strong>{stop.bus_stop_name}</strong>
                              <div>Sıra: {stop.bus_stop_sequence}</div>
                            </div>
                          </Popup>
                        </Marker>
                      ))
                    }
                    
                    {/* Canlı araçlar - güncellenmiş ikonla */}
                    {lineDetail.live_vehicles && lineDetail.live_vehicles
                      .filter(vehicle => vehicle.direction_id === selectedDirection)
                      .map((vehicle) => (
                        <Marker
                          key={vehicle.vehicle_id}
                          position={[vehicle.latitude, vehicle.longitude]}
                          icon={vehicleIcon}
                        >
                          <Popup>
                            <div className={styles.vehiclePopup}>
                              <strong>Araç #{vehicle.vehicle_id}</strong>
                              <div>Son güncelleme: {formatDateTime(vehicle.updated_at)}</div>
                            </div>
                          </Popup>
                        </Marker>
                      ))
                    }
                    
                    {/* Harita sınırlarını ayarla */}
                    <MapController 
                      path={linePath.find(d => d.direction_id === selectedDirection)?.decoded_path}
                      stops={lineStops?.find(d => d.direction_id === selectedDirection)?.bus_stops}
                    />
                  </MapContainer>
                ) : (
                  <div className={styles.noMapDataMessage}>
                    Bu hat için güzergah bilgisi bulunamadı.
                  </div>
                )}
              </div>
              
              <div className={styles.mapLegend}>
                <div className={styles.legendItem}>
                  <div className={styles.legendLine} style={{ backgroundColor: lineDetail.bus_line_color || '#FF0000' }}></div>
                  <span>Hat Güzergahı</span>
                </div>
                <div className={styles.legendItem}>
                  <MdLocationOn className={styles.legendIcon} style={{ color: '#1976d2' }} />
                  <span>Durak</span>
                </div>
                <div className={styles.legendItem}>
                  <MdCommute className={styles.legendIcon} style={{ color: '#1976d2' }} />
                  <span>Araç</span>
                </div>
              </div>
            </div>
            
            {/* SAĞ TARAF - Duraklar */}
            <div className={styles.tabsSection}>
              <div className={styles.sectionHeader}>
                <h3><MdStop /> Duraklar</h3>
              </div>
              
              <div className={styles.tabContent}>
                <div className={styles.stopsTab}>
                  {stopsLoading ? (
                    <div className={styles.loadingTabContent}>
                      <div className={styles.loadingSpinner}></div>
                      <span>Duraklar yükleniyor...</span>
                    </div>
                  ) : (
                    <>
                      {lineStops && lineStops.length > 0 ? (
                        <div className={styles.stopsContainer}>
                          <div className={styles.stopsHeader}>
                            <span>İstikamet: {lineStops.find(dir => dir.direction_id === selectedDirection)?.direction_headsign}</span>
                          </div>
                          
                          <div className={styles.stopsList}>
                            {lineStops
                              .find(dir => dir.direction_id === selectedDirection)?.bus_stops
                              ?.map((stop, index) => (
                                <div 
                                  key={stop.bus_stop_id} 
                                  className={`${styles.stopItem} ${selectedStopId === stop.bus_stop_id.toString() ? styles.selectedStop : ''}`}
                                  onClick={() => handleStopSelect(stop.bus_stop_id.toString())}
                                >
                                  <div className={styles.stopNumber}>{index + 1}</div>
                                  {index < (lineStops.find(dir => dir.direction_id === selectedDirection)?.bus_stops?.length || 1) - 1 && (
                                    <div className={styles.stopConnector}></div>
                                  )}
                                  <div className={styles.stopDetails}>
                                    <div className={styles.stopName}>{stop.bus_stop_name}</div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      ) : (
                        <div className={styles.noDataMessage}>
                          Bu hat için durak bilgisi bulunamadı.
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Alt Kısım - İşletmeci ve Detaylar */}
          <div className={styles.operatorInfoCard}>
            <h3>İşletmeci Bilgileri</h3>
            <div className={styles.operatorContent}>
              <div className={styles.operatorDetails}>
                {lineDetail.operator.operator_logo && (
                  <img 
                    src={lineDetail.operator.operator_logo} 
                    alt={lineDetail.operator.operator_name}
                    className={styles.operatorLogo}
                  />
                )}
                <div className={styles.operatorData}>
                  <div className={styles.operatorName}>{lineDetail.operator.operator_name}</div>
                  {lineDetail.operator.operator_website && (
                    <a href={lineDetail.operator.operator_website} target="_blank" rel="noopener noreferrer" className={styles.operatorLink}>
                      Web Sitesi
                    </a>
                  )}
                  {lineDetail.operator.operator_phone && (
                    <a href={`tel:${lineDetail.operator.operator_phone}`} className={styles.operatorPhone}>
                      {lineDetail.operator.operator_phone}
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default BusLineDetailPage;